import React from "react";
import { logError } from "@/lib/logger";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * Error Boundary component to catch JavaScript errors anywhere in the child component tree
 * and display a fallback UI instead of crashing the whole app.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error using the logger service
    logError("Error boundary caught error", error, {
      componentStack: errorInfo.componentStack,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
    });
    
    this.setState({ errorInfo });
    
    // Store last error for debugging in production
    if (typeof window !== 'undefined') {
      (window as Window & { __lastError?: { error: Error; errorInfo: React.ErrorInfo } }).__lastError = { error, errorInfo };
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI - using inline styles to avoid component dependencies
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
      
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', background: '#f8fafc' }}>
          <div style={{ maxWidth: '500px', width: '100%', background: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', border: '1px solid #ef4444', padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ padding: '0.5rem', background: '#fef2f2', borderRadius: '50%' }}>
                <span style={{ color: '#ef4444', fontSize: '1.5rem' }}>⚠️</span>
              </div>
              <h2 style={{ color: '#ef4444', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Something went wrong</h2>
            </div>
            
            <p style={{ color: '#64748b', marginBottom: '1rem' }}>
              We apologize for the inconvenience. An unexpected error has occurred.
            </p>
            
            {isDev && this.state.error && (
              <div style={{ background: '#f1f5f9', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', overflow: 'auto' }}>
                <p style={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#ef4444', margin: 0 }}>
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#64748b', overflow: 'auto' }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button 
                onClick={this.handleReset}
                style={{ padding: '0.5rem 1rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
              >
                🔄 Try Again
              </button>
              <button 
                onClick={() => window.location.href = '/'}
                style={{ padding: '0.5rem 1rem', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
              >
                🏠 Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component that wraps a component with an ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
) {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
